import { Card, CardContent } from '@/components/ui/card';
import { BookOpen } from 'lucide-react';
import { Bar, BarChart, XAxis, YAxis, CartesianGrid } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import type { GoalSummaryData } from '@/types/goals';
import { cn } from '@/lib/utils';
import {
  SLIDE_PADDING,
  SLIDE_CARD,
  SLIDE_SCROLL_BOTTOM_PAD,
  STORY_CARD_BG,
  NARRATIVE,
  chartConfig,
} from './constants';
import { formatDate } from './utils';
import { StoryCardHeader } from './StoryCardHeader';

export function CardDailyChart({
  summary,
  chartData,
  className,
}: Readonly<{
  summary: GoalSummaryData;
  chartData: Array<{ date: string; pages: number }>;
  className?: string;
}>) {
  return (
    <Card
      className={cn(SLIDE_CARD, 'relative overflow-hidden', STORY_CARD_BG, className)}
    >
      <CardContent
        className={cn(
          'flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-y-contain sm:gap-5',
          SLIDE_PADDING,
          SLIDE_SCROLL_BOTTOM_PAD
        )}
      >
        <StoryCardHeader
          icon={<BookOpen className="h-5 w-5" />}
          title="จำนวนหน้าต่อวัน"
          subtitle={`${formatDate(summary.goal.start_date)} – ${formatDate(summary.goal.end_date)}`}
          narrative={NARRATIVE.dailyChart}
        />

        <ChartContainer
          config={chartConfig}
          className="aspect-auto min-h-[200px] w-full flex-1 overflow-y-auto overflow-x-hidden rounded-2xl border border-border/50 bg-background/30 p-2 shadow-[inset_0_2px_8px_hsl(var(--foreground)/0.03)] scrollbar-hide sm:min-h-[240px] sm:p-3 mix-blend-multiply dark:mix-blend-normal"
          style={{
            maxHeight: 'min(50dvh, 480px)',
            height: Math.min(480, Math.max(200, chartData.length * 18 + 40)),
          }}
        >
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 8, right: 12, left: 4, bottom: 8 }}
          >
            <CartesianGrid
              horizontal={false}
              strokeDasharray="3 3"
              className="stroke-border/30"
            />
            <XAxis
              type="number"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 10 }}
            />
            <YAxis
              type="category"
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 9 }}
              width={52}
            />
            <ChartTooltip
              content={<ChartTooltipContent labelFormatter={String} />}
            />
            <Bar
              dataKey="pages"
              fill="var(--color-pages)"
              radius={[0, 6, 6, 0]}
              barSize={16}
            />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
